import {ReactElement, useState} from "react";
import {useDropzone} from "react-dropzone";
import {Controller} from "react-hook-form";
import styled from "styled-components";

import {uploadFichierContrat} from "@/Api/fichier";
import IconPdf from "@/assets/iconPDF.svg?react";
import IconTrash from "@/assets/iconTrash.svg?react";
import IconUpload from "@/assets/iconUpload.svg?react";
import {Button} from "@/components/Button/Button";
import {ErrorMessage} from "@/components/ErrorMessage/ErrorMessage";
import Loader from "@/components/Loader/Loader";
import {AppContext} from "@/context/AppContext";
import {
  FournisseurContext,
  FournisseurContextType,
} from "@/context/FournisseurContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {breakpoints, quicksandBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {OptionNameValue} from "@/ts/formulaire";

const UploadContratBloc = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  background-color: ${({theme}) => theme.colors.lightPrimary};
  min-height: 100px;
  padding: 10px 20px;
  width: calc(100% - 40px);
  &.disabled-upload-contrat {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      cursor: not-allowed;
    }
  }
  & p,
  & li {
    flex: 1;
    text-align: center;
    color: white;
    font-size: 1.125rem;
    ${quicksandBold}
  }
  &:hover {
    cursor: pointer;
  }
  & #error-content {
    background-color: rgb(190 63 52 / 72%);
  }
`;

const InUpload = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  & img,
  & svg {
    width: 1.5rem;

    fill: white;
    @media (max-widh: ${breakpoints.md}) {
      width: 0.875rem;
    }
  }
  & p {
    flex: none;
  }
`;
const AllFileUpload = styled.ul`
  padding: 0;
`;
const FileUpload = styled.li`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  & svg {
    width: 1.5rem;
    fill: white;
    @media (max-widh: ${breakpoints.md}) {
      width: 0.875rem;
    }
  }
`;
const ContentSendFile = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export function UploadContrat({
  name,
  typeDocument,
  disabled = false,
  currentContremarque = undefined,
}: {
  name: string;
  typeDocument: "contrat-signe" | "annexe";
  disabled?: boolean;
  currentContremarque?: OptionNameValue;
}): ReactElement {
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const {fournisseurAndCampagne, getFournisseur} =
    useGetContext<FournisseurContextType>(
      FournisseurContext,
      "FournisseurContext"
    );
  const {contrat = null} = fournisseurAndCampagne || {};
  if (!contrat) {
    return <ErrorMessage text="No contrat" />;
  }
  const {control, watch, setValue} = useGetContext<HookFormContextType>(
    HookFormContext,
    "HookFormContext"
  );

  const loadingState = useLoadingState();
  const [dragReject, setDragReject] = useState(false);

  const value = watch(name);
  const isUploaded = watch(`${typeDocument}-isUploaded`);

  const handleSubmitFile = async (): Promise<void> => {
    const formData = new FormData();
    formData.append("upload_fichier[fichier][file]", value[0]);
    formData.append("upload_fichier[classe]", typeDocument);
    const response = await loadingState.executeWithLoading(async () => {
      return await uploadFichierContrat(
        API,
        contrat.contrat_id,
        formData,
        currentContremarque?.value
      );
    });
    if (response.statut === "encours") {
      const fichier = response.fichiers.find(
        fichier => fichier.classe === typeDocument
      );
      if (fichier) {
        setValue(`${typeDocument}-isUploaded`, true);
        setValue(`${typeDocument}-fichier_id`, fichier.fichier_id);
      }
    }
    if (response.statut === "actif") {
      getFournisseur();
    }
  };
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({field: {onChange, value}}) => {
        const {getRootProps, getInputProps, isDragActive} = useDropzone({
          accept: {"application/pdf": []},
          maxFiles: 1,
          multiple: false,
          onDrop: acceptedFiles => {
            onChange([...acceptedFiles]);
            setDragReject(false);
          },
          onDragEnter: event => {
            const items = event?.dataTransfer?.items;
            if (!items) return;
            const valid = Array.from(items).every(
              item => item.kind === "file" && item.type === "application/pdf"
            );
            setDragReject(!valid || Array.from(items).length > 1);
          },
          onDragLeave: () => {
            setDragReject(false);
          },
        });
        const handleRemoveFile = (indexToRemove: number): void => {
          const newFiles = value.filter(
            (_: any, index: number) => index !== indexToRemove
          );
          onChange(newFiles);
        };
        return (
          <UploadContratBloc
            {...getRootProps()}
            className={
              disabled || loadingState.isLoading
                ? "disabled-upload-contrat"
                : ""
            }>
            {dragReject ? (
              <p>Fichier non autorisé</p>
            ) : isDragActive ? (
              <InUpload>
                <IconUpload />
                <p>Import du fichier</p>
              </InUpload>
            ) : (
              <>
                {value.length > 0 ? (
                  <ContentSendFile>
                    <AllFileUpload>
                      {value.map(
                        (
                          file: {name?: string; nom?: string},
                          index: number
                        ) => {
                          return (
                            <FileUpload
                              key={index}
                              className="inputFile-element">
                              <IconPdf />
                              {file?.nom || file?.name}

                              <Button
                                icon={IconTrash}
                                variant="negate"
                                disabled={isUploaded}
                                title={
                                  isUploaded
                                    ? "Fichier déjà envoyé"
                                    : "supprimer le fichier"
                                }
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveFile(index);
                                  loadingState.reset();
                                }}
                              />
                            </FileUpload>
                          );
                        }
                      )}
                    </AllFileUpload>

                    <Button
                      label={`Envoyer ${typeDocument === "annexe" ? "l'annexe" : "le contrat"}`}
                      type="button"
                      disabled={isUploaded || loadingState.isLoading}
                      icon={
                        loadingState.isLoading ? (
                          <Loader isIcon={true} color="white" />
                        ) : undefined
                      }
                      title={
                        isUploaded
                          ? "Fichier déjà envoyé"
                          : "Envoyer le fichier"
                      }
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmitFile();
                      }}
                    />
                    {loadingState.hasError ? (
                      <ErrorMessage text={loadingState.error} />
                    ) : null}
                  </ContentSendFile>
                ) : (
                  <>
                    <p>
                      {`EN ATTENTE DE CHARGEMENT DE FICHIER - ${typeDocument === "annexe" ? typeDocument.toUpperCase() + " SIGNEE" : typeDocument.toUpperCase()}`}
                    </p>

                    <Button
                      label={`Charger  ${typeDocument === "annexe" ? "l'annexe" : "le contrat"}`}
                      variant={"secondary"}
                      disabled={disabled}
                      onClick={e => e.preventDefault()}
                    />
                  </>
                )}
              </>
            )}
            <input {...getInputProps()} disabled={disabled} />
          </UploadContratBloc>
        );
      }}
    />
  );
}
